import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, ChefHat, Coffee, Utensils } from 'lucide-react';
import heroImage from '../assets/images/hero.jpg';
import ballroomImage from '../assets/images/ballroom.jpg';
import satayImage from '../assets/images/satay.jpg';
import steakImage from '../assets/images/steak.jpg';
import coffeeImage from '../assets/images/coffee.jpg';

const Home = () => {
  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center text-center text-white">
        <div className="absolute inset-0">
          <img 
            src={heroImage} 
            alt="Nawasena Garden Resto" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <h2 className="text-lg md:text-xl font-medium tracking-[0.2em] mb-4 uppercase text-emerald-400">Selamat Datang di</h2>
          <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 leading-tight">
            Nawasena <br/> Garden Resto
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto mb-10 font-light">
            Menikmati hidangan lezat dengan suasana taman yang asri dan ballroom mewah untuk momen spesial Anda.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/menu" className="bg-emerald-600 text-white px-8 py-3 rounded-full font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2">
              Lihat Menu <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-3 rounded-full font-medium hover:bg-white/20 transition-colors">
              Reservasi Meja
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-stone-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center group">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                <ChefHat size={32} />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3">Chef Profesional</h3>
              <p className="text-gray-600">Hidangan disiapkan oleh chef berpengalaman dengan bahan-bahan pilihan berkualitas tinggi.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center group">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                <Utensils size={32} />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3">Menu Beragam</h3>
              <p className="text-gray-600">Pilihan menu lengkap mulai dari masakan Nusantara, Western, hingga aneka kopi spesial.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center group">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                <Coffee size={32} />
              </div>
              <h3 className="text-xl font-serif font-bold mb-3">Suasana Nyaman</h3>
              <p className="text-gray-600">Nikmati waktu bersantai Anda di area taman yang sejuk atau ballroom yang elegan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlight Menu */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-emerald-600 font-medium tracking-widest uppercase mb-2">Favorit Pelanggan</h2>
            <h3 className="text-4xl font-serif font-bold text-gray-900">Menu Unggulan Kami</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Item 1 */}
            <div className="group relative overflow-hidden rounded-xl h-[400px]">
              <img src={satayImage} alt="Sate Maranggi" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-white">
                <h4 className="text-2xl font-serif font-bold mb-1">Sate Maranggi</h4>
                <p className="text-gray-300 text-sm mb-2">Authentic Purwakarta beef satay</p>
                <span className="text-emerald-400 font-bold">Rp 45.000</span>
              </div>
            </div>

            {/* Item 2 */}
            <div className="group relative overflow-hidden rounded-xl h-[400px]">
              <img src={steakImage} alt="Steak" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-white">
                <h4 className="text-2xl font-serif font-bold mb-1">Tenderloin Steak</h4>
                <p className="text-gray-300 text-sm mb-2">Grilled tenderloin steak</p>
                <span className="text-emerald-400 font-bold">Rp 120.000</span>
              </div>
            </div>

            {/* Item 3 */}
            <div className="group relative overflow-hidden rounded-xl h-[400px]">
              <img src={coffeeImage} alt="Signature Coffee" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-white">
                <h4 className="text-2xl font-serif font-bold mb-1">Hazelnut Latte</h4>
                <p className="text-gray-300 text-sm mb-2">Hazelnut with coffee milk</p>
                <span className="text-emerald-400 font-bold">Rp 30.000</span>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/menu" className="inline-block border-2 border-emerald-600 text-emerald-600 px-8 py-3 rounded-full font-medium hover:bg-emerald-600 hover:text-white transition-colors">
              Lihat Semua Menu
            </Link>
          </div>
        </div>
      </section>

      {/* Ballroom / About Teaser */}
      <section className="py-20 bg-stone-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={ballroomImage} alt="Ballroom" className="w-full h-full object-cover" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-emerald-400 font-medium tracking-widest uppercase mb-4">Ballroom & Events</h2>
            <h3 className="text-4xl md:text-5xl font-serif font-bold mb-8">Rayakan Momen Spesial Anda</h3>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed">
              Nawasena Garden Resto & Ballroom menyediakan tempat yang sempurna untuk pernikahan, ulang tahun, gathering perusahaan, dan acara spesial lainnya. Dengan kapasitas luas dan interior elegan, kami siap mewujudkan acara impian Anda.
            </p>
            <Link to="/contact" className="bg-emerald-600 text-white px-8 py-3 rounded-full font-medium hover:bg-emerald-700 transition-colors">
              Hubungi Kami untuk Event
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
